import React, { useEffect, useState } from "react";
import { Modal } from "@mantine/core";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Polygon,
  Popup,
  useMap,
} from "react-leaflet";
import { kml } from "@tmcw/togeojson";

const containerStyle = {
  width: "100%",
  height: "500px",
};

const initialCenter = [19.24924667870585, 72.85882726308839];

// ✅ Fit bounds helper
const FitBounds = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [bounds, map]);

  return null;
};

const KmlMapModal = ({ opened, onClose, kmlUrl }) => {
  const [geojsonData, setGeojsonData] = useState([]);
  const [bounds, setBounds] = useState(null);

  // ✅ Fetch and parse KML from URL
  useEffect(() => {
    if (!kmlUrl || !opened) return;

    const fetchKmlData = async () => {
      try {
        // console.log("Fetching KML from:", kmlUrl);

        const response = await fetch(kmlUrl);

        // Handle 304 Not Modified - use cached version or retry with cache bypass
        if (response.status === 304) {
          console.warn("KML file not modified (304), retrying with cache bypass...");
          const retryResponse = await fetch(kmlUrl, { headers: { "Cache-Control": "no-cache" } });
          
          if (!retryResponse.ok) {
            throw new Error(`Failed to fetch KML file: ${retryResponse.status} ${retryResponse.statusText}`);
          }
          
          const kmlText = await retryResponse.text();
          processKmlText(kmlText);
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch KML file: ${response.status} ${response.statusText}`);
        }

        const kmlText = await response.text();
        processKmlText(kmlText);
      } catch (error) {
        console.error("KML load error:", error);
      }
    };

    // ✅ Process KML text into GeoJSON
    const processKmlText = (kmlText) => {
      const parser = new DOMParser();
      const kmlDoc = parser.parseFromString(kmlText, "text/xml");

      const geojson = kml(kmlDoc);

      console.log("Parsed GeoJSON:", geojson);

      if (!geojson.features.length) {
        console.warn("No features found in KML");
        setGeojsonData([]);
        return;
      }

      setGeojsonData(geojson.features);
      calculateBounds(geojson.features);
    };

    fetchKmlData();
  }, [kmlUrl, opened]);

  // ✅ Reset when modal closes
  useEffect(() => {
    if (!opened) {
      setGeojsonData([]);
      setBounds(null);
    }
  }, [opened]);

  // ✅ Calculate bounds for auto-fit
  const calculateBounds = (features) => {
    const coordinates = features.flatMap((feature) => {
      const geom = feature.geometry;
      if (!geom) return [];

      if (geom.type === "Point") return [geom.coordinates];
      if (geom.type === "LineString") return geom.coordinates;
      if (geom.type === "Polygon") return geom.coordinates.flat();

      return [];
    });

    if (!coordinates.length) return;

    const latlngs = coordinates.map(([lng, lat]) => [lat, lng]);
    setBounds(latlngs);
  };

  // ✅ Distance (Haversine)
  const calculateDistance = (path) => {
    let total = 0;

    for (let i = 0; i < path.length - 1; i++) {
      total += haversine(path[i], path[i + 1]);
    }

    return total.toFixed(2) + " meters";
  };

  // ✅ Perimeter
  const calculatePerimeter = (coords) => {
    let total = 0;

    for (let i = 0; i < coords.length - 1; i++) {
      total += haversine(coords[i], coords[i + 1]);
    }

    return total.toFixed(2) + " meters";
  };

  // ✅ Polygon area (Shoelace + Earth correction)
  const calculateArea = (coords) => {
    const latlngs = coords.map(([lng, lat]) => [lat, lng]);
    const area = polygonArea(latlngs);
    return area.toFixed(2) + " sq meters";
  };

  // 🌍 Haversine formula
  const haversine = ([lng1, lat1], [lng2, lat2]) => {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) ** 2;

    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // 🧮 Polygon area
  const polygonArea = (latlngs) => {
    let area = 0;
    const R = 6378137;

    for (let i = 0; i < latlngs.length; i++) {
      const [lat1, lng1] = latlngs[i];
      const [lat2, lng2] = latlngs[(i + 1) % latlngs.length];

      area +=
        toRad(lng2 - lng1) *
        (2 +
          Math.sin(toRad(lat1)) +
          Math.sin(toRad(lat2)));
    }

    area = (area * R * R) / 2;
    return Math.abs(area);
  };

  const toRad = (d) => (d * Math.PI) / 180;

  return (
    <Modal opened={opened} onClose={onClose} title="KML Document" size="xl">
      <MapContainer
        center={initialCenter}
        zoom={12}
        style={containerStyle}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {bounds && <FitBounds bounds={bounds} />}

        {geojsonData.map((feature, index) => {
          const geom = feature.geometry;
          const name = feature.properties?.name || "Unnamed";

          if (!geom) return null;

          // 🔵 POINT
          if (geom.type === "Point") {
            const [lng, lat] = geom.coordinates;

            return (
              <Marker key={index} position={[lat, lng]}>
                <Popup>
                  <p>{name}</p>
                  <p>
                    {lat}, {lng}
                  </p>
                </Popup>
              </Marker>
            );
          }

          // 🔴 LINESTRING
          if (geom.type === "LineString") {
            const positions = geom.coordinates.map(([lng, lat]) => [lat, lng]);

            return (
              <Polyline key={index} positions={positions}>
                <Popup>
                  <p>{name}</p>
                  <p>Distance: {calculateDistance(geom.coordinates)}</p>
                </Popup>
              </Polyline>
            );
          }

          // 🔵 POLYGON
          if (geom.type === "Polygon") {
            const positions = geom.coordinates[0].map(([lng, lat]) => [lat, lng]);

            return (
              <Polygon key={index} positions={positions}>
                <Popup>
                  <p>{name}</p>
                  <p>Perimeter: {calculatePerimeter(geom.coordinates[0])}</p>
                  <p>Area: {calculateArea(geom.coordinates[0])}</p>
                </Popup>
              </Polygon>
            );
          }

          return null;
        })}
      </MapContainer>
    </Modal>
  );
};

export default KmlMapModal;
